/**
 * 「承認待ち」のカード（ADR-0030 の 1・2）。会話の上のカード欄の一番上と、承認待ちが生まれた返事のすぐ下に出す。
 * 中身は全て Gateway が DB の行からコードで作ったもの。上から「実際に行う操作」（機械）→ 入力の全部 → 期限 → きっかけ →
 * 「Jarvis の説明」（LLM の文。文字として表示し、HTML として解釈しない）→ ボタン。
 * 答えはボタンだけで受ける（会話の「はい」では受けない）。critical は、その承認 1 件に結び付けたパスキーの再認証をしてから送る。
 */
import { startAuthentication } from "@simplewebauthn/browser";
import { ApiError, api } from "./api.js";

interface ApprovalCard {
  approval_id: string;
  proposal_sha256: string;
  created_at: string;
  expires_at: string;
  no_expiry: boolean;
  requires_identity_check: boolean;
  tool: string;
  capability: string;
  scope: string;
  risk: string;
  machine_summary: string;
  params: Array<{ key: string; value: string }>;
  /** 変更の前後の差分（自分の設定を変える提案など。ADR-0065）。無ければ null */
  diff?: string | null;
  trigger_text: string | null;
  rationale: string;
}
interface ApprovalResult {
  approval_id: string;
  machine_summary: string;
  status: "approved" | "rejected" | "expired";
  decided_at: string | null;
  execution: {
    status: "running" | "executed" | "failed" | "unknown" | "skipped";
    error_code: string | null;
    finished_at: string | null;
    result_text: string | null;
  } | null;
}
export interface ApprovalsView {
  trace_id: string | null;
  now: string;
  frozen: boolean;
  pending: ApprovalCard[];
  recent: ApprovalResult[];
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  // 文字列は Text ノードとして入れる（LLM の文・入力の値を HTML として解釈しない）
  e.append(...children);
  return e;
}

/** 残り時間。端末の時計ではなく、Gateway の now と expires_at の差から（開いてからの経過だけ端末で足す） */
function remaining(card: ApprovalCard, serverNow: string, openedAt: number): string {
  if (card.no_expiry) return "期限なし";
  const left =
    new Date(card.expires_at).getTime() - new Date(serverNow).getTime() - (Date.now() - openedAt);
  const at = new Date(card.expires_at).toLocaleTimeString("ja-JP", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (left <= 0) return `期限切れ（${at} まで）`;
  const min = Math.floor(left / 60_000);
  const text = min >= 60 ? `あと ${Math.floor(min / 60)} 時間 ${min % 60} 分` : `あと ${min} 分`;
  return `${text}（${at} まで）`;
}

const RESULT_LABEL: Record<string, string> = {
  executed: "実行した",
  failed: "失敗した",
  unknown: "結果不明（実際に行われたか確かめてください）",
  running: "実行中",
  skipped: "実行しなかった（前の操作が止まったため）",
};

function resultLabel(r: ApprovalResult): string {
  if (r.status === "rejected") return "却下した";
  if (r.status === "expired") return "期限切れ（実行していない）";
  if (!r.execution) return "承認したが実行されなかった（安全層が止めた）";
  return RESULT_LABEL[r.execution.status] ?? r.execution.status;
}

const ERROR_TEXT: Record<string, string> = {
  stale: "表示した内容が変わっていました。カードを出し直します。",
  already_decided: "もう答えてあります。",
  expired: "期限が切れていました（実行していません）。",
  frozen: "凍結中のため、今は承認できません。",
  identity_required: "本人確認（パスキー）が必要です。",
  identity_check_failed: "本人確認に失敗しました。",
  passkey_session_required: "パスキーで登録した端末からだけ答えられます。",
  voice_device_not_allowed: "声の端末からは答えられません。",
  not_executed: "承認しましたが、安全層が実行を止めました（実行していません）。",
};

/** 変更の前後の差分（ADR-0065）。行ごとに +（足す）/ −（消す）を色分けする。中身は文字として入れる */
function diffElement(diff: string): HTMLElement {
  return el(
    "div",
    { class: "diff" },
    el("p", { class: "small muted" }, "変更の前後（− が今の版、+ が新しい版）"),
    el(
      "pre",
      { class: "diff-lines" },
      ...diff.split("\n").map((line) =>
        el(
          "span",
          {
            class: line.startsWith("+")
              ? "add"
              : line.startsWith("-")
                ? "del"
                : line.startsWith("@@")
                  ? "hunk"
                  : "ctx",
          },
          `${line}\n`,
        ),
      ),
    ),
  );
}

/** カード 1 枚 */
function cardElement(
  c: ApprovalCard,
  view: ApprovalsView,
  openedAt: number,
  onDone: () => void,
): HTMLElement {
  const status = el("p", { class: "small muted" });
  const due = el("p", { class: "small" }, remaining(c, view.now, openedAt));
  const buttons: HTMLButtonElement[] = [];
  const send = async (decision: "approved" | "rejected") => {
    for (const b of buttons) b.disabled = true;
    status.textContent = decision === "approved" ? "実行しています…" : "却下しています…";
    try {
      let identity: Record<string, unknown> = {};
      if (decision === "approved" && c.requires_identity_check) {
        const o = await api<{
          options: Parameters<typeof startAuthentication>[0]["optionsJSON"];
          challenge_token: string;
        }>("/v1/approvals/identity/options", {
          approval_id: c.approval_id,
          proposal_sha256: c.proposal_sha256,
        });
        const assertion = await startAuthentication({ optionsJSON: o.options });
        identity = { challenge_token: o.challenge_token, assertion };
      }
      const r = await api<{ execution: { status: string } | null }>("/v1/approvals/decide", {
        approval_id: c.approval_id,
        decision,
        proposal_sha256: c.proposal_sha256,
        ...identity,
      });
      status.textContent =
        decision === "rejected"
          ? "却下しました。"
          : `承認しました: ${RESULT_LABEL[r.execution?.status ?? ""] ?? "記録しました"}`;
      onDone();
    } catch (e) {
      const code = e instanceof ApiError ? e.code : e instanceof Error ? e.name : String(e);
      status.textContent =
        ERROR_TEXT[code] ??
        (code === "NotAllowedError" ? "キャンセルしました。" : `答えを送れませんでした（${code}）`);
      if (
        code === "stale" ||
        code === "already_decided" ||
        code === "expired" ||
        code === "not_executed"
      )
        onDone();
      else for (const b of buttons) b.disabled = false;
    }
  };
  const approve = el(
    "button",
    { class: "primary" },
    c.requires_identity_check ? "本人確認して承認" : "承認して実行",
  ) as HTMLButtonElement;
  const reject = el("button", {}, "却下") as HTMLButtonElement;
  approve.onclick = () => void send("approved");
  reject.onclick = () => void send("rejected");
  buttons.push(approve, reject);
  if (view.frozen) approve.disabled = true;

  return el(
    "div",
    { class: "approval-item", "data-approval-id": c.approval_id },
    el(
      "p",
      { class: "approval-head" },
      "実際に行う操作",
      el("span", { class: `risk ${c.risk}` }, ` ${c.risk} / ${c.scope}`),
    ),
    el("pre", { class: "machine" }, c.machine_summary),
    c.diff ? diffElement(c.diff) : "",
    el(
      "dl",
      { class: "params" },
      ...c.params.flatMap((p) => [el("dt", {}, p.key), el("dd", {}, el("code", {}, p.value))]),
    ),
    due,
    c.trigger_text ? el("p", { class: "small muted" }, `きっかけ: ${c.trigger_text}`) : "",
    el(
      "div",
      { class: "rationale" },
      el(
        "p",
        { class: "small muted" },
        "Jarvis の説明（Jarvis が書いた文で、上の操作と食い違うことがあります）",
      ),
      el("p", { class: "small" }, c.rationale || "（説明なし）"),
    ),
    el("div", { class: "row" }, approve, reject),
    status,
  );
}

/**
 * カード欄を描く。only を渡すと、その承認だけ（返事のすぐ下に出すとき）。何も無ければ空にする。
 * 声の端末（403）・未適用の環境（404 / 500）では出さない。
 */
export async function renderApprovalsCard(
  root: HTMLElement,
  opts: {
    only?: string[];
    onChange?: () => void;
    /** 読めたとき、承認待ちの全数と凍結を知らせる（見出しの状態の行に使う。ADR-0055） */
    onView?: (v: { pending: number; frozen: boolean }) => void;
    /**
     * 何を出すか。pending = 承認待ちだけ（会話の上の欄。答えたら消える）/ recent = 結果だけ（引き出し）/ all = 両方（返事の下）。
     * 2026-10-11: 答えた後も結果が会話の上に残り、会話が見にくかった
     */
    show?: "pending" | "recent" | "all";
  } = {},
): Promise<void> {
  let view: ApprovalsView;
  try {
    view = await api<ApprovalsView>("/v1/approvals/pending");
  } catch (e) {
    if (!(e instanceof ApiError)) console.warn(e);
    root.replaceChildren();
    return;
  }
  opts.onView?.({ pending: view.pending.length, frozen: view.frozen });
  const openedAt = Date.now();
  const redraw = () => {
    void renderApprovalsCard(root, opts);
    opts.onChange?.();
  };
  const show = opts.show ?? "all";
  const pending =
    show === "recent"
      ? []
      : opts.only
        ? view.pending.filter((c) => opts.only?.includes(c.approval_id))
        : view.pending;
  const recent =
    show === "pending"
      ? []
      : opts.only
        ? view.recent.filter((r) => opts.only?.includes(r.approval_id))
        : view.recent;
  if (pending.length === 0 && recent.length === 0) {
    root.replaceChildren();
    return;
  }
  root.replaceChildren(
    el(
      "section",
      { class: `card approvals${pending.length > 0 ? " waiting" : ""}` },
      el(
        "h2",
        { class: "jv-latin approvals-title" },
        pending.length > 0 ? `承認してください · ${pending.length} 件` : "承認の結果",
      ),
      view.frozen ? el("p", { class: "small flag" }, "凍結中のため、今は承認できません。") : "",
      ...pending.map((c) => cardElement(c, view, openedAt, redraw)),
      recent.length > 0
        ? el(
            "ul",
            { class: "recent small" },
            ...recent.map((r) =>
              el(
                "li",
                {},
                `${resultLabel(r)}: `,
                el("code", {}, r.machine_summary),
                r.execution?.result_text
                  ? el("span", { class: "muted" }, ` → ${r.execution.result_text}`)
                  : "",
              ),
            ),
          )
        : "",
    ),
  );
}
